/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { CardPageHeader } from "ctech-react-components";
import { Breadcrumbs, Button, Link, Typography } from "@mui/material";

import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { handleGetDealerUsersRequest } from "store/modules/DealerUsers/thunk.store";
import {
  DealerUserRecoveryPasswordRequest,
  DealerUsersProps,
} from "store/modules/DealerUsers/types.store";
import manWomanBoxes from "../../assets/supergasbras/images/manWomanBoxes.svg";
import * as Styled from "./styles";
import TableListUsers from "./components/TableListUsers";
import ModalAddUser from "./components/ModalAddUser";

import ModalEditUser from "./components/ModalEditUser";
import ModalTemporaryPassword from "./components/ModalTemporaryPassword";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import authApi from "services/authApi";
import ModalConfirmDeleteUser from "./components/ModalConfirmDeleteUser";

type UsersProps = {
  dealer: {
    id: string;
    name: string;
  };
};

const ManageUsers = () => {
  const location = useLocation();
  const { dealer } = location.state as UsersProps;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    data,
    pagination: { totalCount },
  } = useAppSelector((state) => state.dealerUsers);
  const [isAddNewUserModalVisible, setAddNewUserModalVisible] = useState(false);
  const [isEditUserModalVisible, setEditUserModalVisible] = useState(false);
  const [
    isSendedTemporaryPasswordModalVisible,
    setSendedTemporaryPasswordModalVisible,
  ] = useState(false);
  const [user, setUser] = useState<DealerUsersProps | null>(null);
  const [userEmailOrCPF, setUserEmailOrCPF] = useState<string | null>(null);
  const [typeProfile, setTypeProfile] = useState<string | undefined>(undefined);
  const [userDeleteModalVisible, setUserDeleteModalVisible] = useState(false);

  const handleAddNewAreaModalVisibility = () => {
    setAddNewUserModalVisible((state) => !state);
  };

  const handleEditUserModalVisibility = (dataUser: DealerUsersProps) => {
    setUser(dataUser);
    setEditUserModalVisible((state) => !state);
  };

  const handleSendedTemporaryPasswordModalVisibility = async (
    dataUser: DealerUserRecoveryPasswordRequest,
  ) => {
    try {
      dispatch(setIsSpinnerVisible(true));
      const DataBody = dataUser.email
        ? {
            email: dataUser.email,
            scope: "Dealer",
          }
        : {
            cpf: dataUser.cpf,
            scope: "Dealer",
          };

      setTypeProfile(dataUser.scope);

      const { data: dataResponse } = await authApi.put(
        `/v1/users/password/temporary`,
        DataBody,
      );

      setUserEmailOrCPF(dataUser.cpf ? dataResponse : dataUser.email);

      setSendedTemporaryPasswordModalVisible(
        !isSendedTemporaryPasswordModalVisible,
      );
      dispatch(setIsSpinnerVisible(false));
    } catch (error) {
      dispatch(setIsSpinnerVisible(false));
    }
  };

  const [page, setPage] = useState({
    limit: 20,
    offset: 0,
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage({ ...page, offset: newPage || 1 - 1 });
  };

  const handleRowsPerPageChange = (rows: number) => {
    setPage({ ...page, limit: rows });
  };

  const backPage = (index: number) => navigate(index);

  const searchUsers = useCallback(async () => {
    await dispatch(
      handleGetDealerUsersRequest({ dealerId: dealer.id, ...page }),
    );
  }, [dealer.id, dispatch, page]);

  const handleDeleteUser = useCallback(async () => {
    setUserDeleteModalVisible(!userDeleteModalVisible);
  }, [userDeleteModalVisible]);

  useEffect(() => {
    searchUsers();
  }, [dispatch, page, dealer, searchUsers]);

  return (
    <Styled.Container>
      <Styled.Content>
        <Styled.Row>
          <Button
            color="inherit"
            type="button"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => backPage(-1)}
          >
            Voltar
          </Button>
        </Styled.Row>
        <ModalAddUser
          dealerId={dealer.id}
          isOpen={isAddNewUserModalVisible}
          onClose={handleAddNewAreaModalVisibility}
          onCloseSuccess={() => {
            dispatch(
              handleGetDealerUsersRequest({
                dealerId: dealer.id,
                ...page,
              }),
            );
          }}
        />
        <ModalEditUser
          user={user}
          isOpen={isEditUserModalVisible}
          onClose={() => setEditUserModalVisible(!isEditUserModalVisible)}
          passwordTemporary={handleSendedTemporaryPasswordModalVisibility}
          deleteUser={handleDeleteUser}
        />
        <ModalConfirmDeleteUser
          user={user}
          isOpen={userDeleteModalVisible}
          onClose={() => {
            setUserDeleteModalVisible(!userDeleteModalVisible);
          }}
        />
        <ModalTemporaryPassword
          typeProfile={typeProfile}
          email={userEmailOrCPF}
          isOpen={isSendedTemporaryPasswordModalVisible}
          onClose={() =>
            setSendedTemporaryPasswordModalVisible(
              !isSendedTemporaryPasswordModalVisible,
            )
          }
        />
        <CardPageHeader
          urlImage={manWomanBoxes}
          labelButton={t("addUser")}
          actionButton={handleAddNewAreaModalVisibility}
          title={
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                underline="hover"
                color="inherit"
                onClick={() => backPage(-2)}
              >
                {t("dealers")}
              </Link>
              <Link
                underline="hover"
                color="inherit"
                onClick={() => backPage(-1)}
              >
                {dealer.name}
              </Link>
              <Typography color="text.primary" variant="h5">
                {t("manageUsers")}
              </Typography>
            </Breadcrumbs>
          }
          buttonEndIcon={<AddIcon />}
        />
        <TableListUsers
          data={data}
          rowsPerPageOptions={[20]}
          rowsPerPage={page.limit}
          page={page.offset}
          totalCount={totalCount}
          onPageChange={handleChangePage}
          onRowsPerPageChange={({ target }) =>
            handleRowsPerPageChange(Number(target.value))
          }
          itemActions={{
            edit: handleEditUserModalVisibility,
            passwordTemporary: handleSendedTemporaryPasswordModalVisibility,
          }}
        />
      </Styled.Content>
    </Styled.Container>
  );
};

export default ManageUsers;
